import React from "react";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { MaterialTransferPage } from "../../../../models/materialStockTransfer.model";
import PaginationControls from "../../../../utils/PaginationControls";
import MaterialTransferCard from "./MaterialTransferCard";

type Direction = "incoming" | "outgoing";

interface MaterialTransferListProps {
  direction: Direction;
  data: MaterialTransferPage & { loading: boolean };
  actionLoading: boolean;
  isAdmin: boolean;
  userId: string;
  onPageChange: (page: number) => void;
  onAccept: (id: string, remark: string) => Promise<boolean>;
  onCancel: (id: string, remark: string) => Promise<boolean>;
}

const MaterialTransferList: React.FC<MaterialTransferListProps> = ({
  direction,
  data,
  actionLoading,
  isAdmin,
  userId,
  onPageChange,
  onAccept,
  onCancel,
}) => {
  const isIncoming = direction === "incoming";
  const HeadingIcon = isIncoming ? ArrowDownLeft : ArrowUpRight;
  const heading = isIncoming
    ? "Incoming material transfers"
    : "Outgoing material transfers";

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <header className="flex items-center justify-between gap-3 border-b border-gray-100 p-4 sm:p-6">
        <h2 className="flex items-center gap-2 text-lg font-bold text-gray-800 sm:text-xl">
          <HeadingIcon
            size={20}
            className={isIncoming ? "text-amber-600" : "text-blue-600"}
          />
          {heading}
        </h2>
        <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-700">
          {data.total}
        </span>
      </header>

      {data.loading && data.transfers.length === 0 ? (
        <div className="px-4 py-12 text-center text-sm text-gray-500">
          Loading {direction} material transfers...
        </div>
      ) : data.transfers.length === 0 ? (
        <div className="px-4 py-12 text-center">
          <p className="font-semibold text-gray-800">
            No pending {direction} material transfers
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {isIncoming
              ? "Transfers to this warehouse will appear here."
              : "Transfers sent from this warehouse will appear here."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200 text-left lg:min-w-[1450px]">
            <thead className="hidden bg-gray-50 text-xs font-semibold uppercase text-gray-500 lg:table-header-group">
              <tr>
                <th className="px-4 py-3">Material / SKU</th>
                <th className="px-4 py-3">Quantity</th>
                <th className="px-4 py-3">Warehouse route</th>
                <th className="px-4 py-3">Sent by / date</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {data.transfers.map((transfer) => (
                <MaterialTransferCard
                  key={transfer._id}
                  transfer={transfer}
                  direction={direction}
                  isAdmin={isAdmin}
                  userId={userId}
                  actionLoading={actionLoading}
                  onAccept={onAccept}
                  onCancel={onCancel}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.transfers.length > 0 && (
        <div className="border-t border-gray-200">
          <PaginationControls
            currentPage={data.page}
            totalPages={data.totalPages || 1}
            onPageChange={onPageChange}
            isLoading={data.loading}
            totalItems={data.total}
          />
        </div>
      )}
    </section>
  );
};

export default MaterialTransferList;
